// src/admin/pages/QuestionBankManagement.jsx
import React, { useState, useMemo } from "react";
import {
  Folder,
  FolderOpen,
  ChevronRight,
  CheckSquare,
  PenTool,
  Plus,
  Upload,
  Search,
  Eye,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle2,
  MoveRight,
  ArrowLeft,
  Calendar,
  Sparkles,
  Copy,
} from "lucide-react";
import { useAdminData } from "../context/AdminDataContext";
import { qbAPI } from "../services/api";
import Modal from "../components/Modal";
import {
  prelimsSubjectFolders,
  mainsStructure,
} from "../data/initialQuestionBankData";

const samplePrelimsRawText = `Q1.) With reference to the Indian Constitution, consider the following statements regarding the Attorney-General for India:
(A) He is appointed by the Prime Minister of India.
(B) He has the right of audience in all courts in the territory of India.
(C) He holds office during the pleasure of the Chief Justice of India.
(D) He cannot take part in proceedings of either House of Parliament.
Options

<CO> B

Q2.) Which of the following bodies is responsible for recommending the distribution of net proceeds of taxes between the Union and the States?
(A) NITI Aayog
(B) Finance Commission
(C) Goods and Services Tax Council
(D) Inter-State Council
Options

<CO> B

Q3.) With reference to Ancient India, the term 'Nishka' denoted which of the following?
(A) An irrigation tax levied on rice cultivators
(B) A gold ornament later used as a currency denomination
(C) A guild of royal ivory craftsmen
(D) A treatise on medicinal herbs
Options

<CO> B`;

const sampleMainsRawText = `Q1.) Critically evaluate the efficacy of the Collegium system in judicial appointments in India. Suggest viable alternative institutional reforms.
Reference: Structure answer with constitutional background of Articles 124 & 217, Three Judges Cases, arguments for collegium autonomy vs lack of transparency, and National Judicial Appointments Commission (NJAC) alternatives with a permanent secretariat.

Q2.) Examine the socioeconomic ramifications of climate-induced displacement in the coastal belts of India.
Reference: Discuss coastal erosion in Sundarbans and Odisha, loss of artisanal fishing livelihoods, urban distress migration, and Sendai Framework disaster-resilient infrastructure.`;

const QuestionBankManagement = () => {
  const {
    prelimsQuestionBank,
    addPrelimsQuestion,
    updatePrelimsQuestion,
    deletePrelimsQuestion,
    movePrelimsQuestions,
    bulkDeletePrelimsQuestions,
    bulkAddPrelimsQuestions,

    mainsQuestionBank,
    addMainsQuestion,
    updateMainsQuestion,
    deleteMainsQuestion,
    moveMainsQuestions,
    bulkDeleteMainsQuestions,
    bulkAddMainsQuestions,
    dbFolders,
    refreshAllData,
  } = useAdminData();

  // Active top tab: 'prelims' | 'mains'
  const [activeTab, setActiveTab] = useState("prelims");

  // Navigation state (null = root folder view; string/object = opened folder)
  const [activePrelimsFolder, setActivePrelimsFolder] = useState(null);
  const [activeMainsFolder, setActiveMainsFolder] = useState(null); // { gsPaper, subject }

  // Folder search & filters inside an opened folder
  const [folderSearch, setFolderSearch] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("All");

  // Selected questions for batch operations inside an opened folder
  const [selectedIds, setSelectedIds] = useState([]);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

  // Move Modal
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [moveSingleItem, setMoveSingleItem] = useState(null);
  const [targetSubject, setTargetSubject] = useState("");
  const [targetGsPaper, setTargetGsPaper] = useState("GS Paper I");

  // Form states for manual question add / edit
  const [prelimsForm, setPrelimsForm] = useState({
    questionText: "",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctAnswer: "A",
    topic: "",
    difficulty: "Medium",
    explanation: "",
  });

  const [mainsForm, setMainsForm] = useState({
    questionText: "",
    topic: "",
    maxMarks: 15,
    wordLimit: 250,
    difficulty: "Medium",
    referenceAnswer: "",
  });

  // Bulk Upload states
  const [bulkRawText, setBulkRawText] = useState("");
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkParsedQuestions, setBulkParsedQuestions] = useState([]);
  const [isBulkParsed, setIsBulkParsed] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(false);

  // --------------------------------------------------------------------------
  // Helper functions: Counts & Dates for Folders
  // --------------------------------------------------------------------------
  const getPrelimsFolderCount = (subjectName) => {
    return (prelimsQuestionBank || []).filter(
      (q) => (q?.subject || "").trim().toLowerCase() === subjectName.toLowerCase()
    ).length;
  };

  const getPrelimsFolderLastDate = (subjectName) => {
    const qs = (prelimsQuestionBank || []).filter(
      (q) => (q?.subject || "").trim().toLowerCase() === subjectName.toLowerCase()
    );
    if (qs.length === 0) return "No questions yet";
    return qs[0]?.dateAdded || "Recent";
  };

  const getMainsFolderCount = (gsPaper, subjectName) => {
    return (mainsQuestionBank || []).filter(
      (q) =>
        (q?.gsPaper || "").trim().toLowerCase() === gsPaper.toLowerCase() &&
        (q?.subject || "").trim().toLowerCase() === subjectName.toLowerCase()
    ).length;
  };

  const getMainsFolderLastDate = (gsPaper, subjectName) => {
    const qs = (mainsQuestionBank || []).filter(
      (q) =>
        (q?.gsPaper || "").trim().toLowerCase() === gsPaper.toLowerCase() &&
        (q?.subject || "").trim().toLowerCase() === subjectName.toLowerCase()
    );
    if (qs.length === 0) return "No questions yet";
    return qs[0]?.dateAdded || "Recent";
  };

  // Filtered questions inside current folder
  const currentQuestions = useMemo(() => {
    if (activeTab === "prelims") {
      if (!activePrelimsFolder) return [];
      return (prelimsQuestionBank || []).filter((q) => {
        const matchesSubject =
          (q?.subject || "").trim().toLowerCase() === activePrelimsFolder.toLowerCase();
        const matchesSearch =
          !folderSearch.trim() ||
          (q?.questionText || "").toLowerCase().includes(folderSearch.toLowerCase()) ||
          (q?.topic || "").toLowerCase().includes(folderSearch.toLowerCase());
        const matchesDiff =
          difficultyFilter === "All" || (q?.difficulty || "Medium") === difficultyFilter;
        return matchesSubject && matchesSearch && matchesDiff;
      });
    } else {
      if (!activeMainsFolder) return [];
      return (mainsQuestionBank || []).filter((q) => {
        const matchesPaper =
          (q?.gsPaper || "").trim().toLowerCase() === activeMainsFolder.gsPaper.toLowerCase();
        const matchesSubject =
          (q?.subject || "").trim().toLowerCase() === activeMainsFolder.subject.toLowerCase();
        const matchesSearch =
          !folderSearch.trim() ||
          (q?.questionText || "").toLowerCase().includes(folderSearch.toLowerCase()) ||
          (q?.topic || "").toLowerCase().includes(folderSearch.toLowerCase());
        const matchesDiff =
          difficultyFilter === "All" || (q?.difficulty || "Medium") === difficultyFilter;
        return matchesPaper && matchesSubject && matchesSearch && matchesDiff;
      });
    }
  }, [
    activeTab,
    activePrelimsFolder,
    activeMainsFolder,
    prelimsQuestionBank,
    mainsQuestionBank,
    folderSearch,
    difficultyFilter,
  ]);

  // --------------------------------------------------------------------------
  // Bulk Upload Parsing: Automatically belongs to currently open folder!
  // --------------------------------------------------------------------------
  const parsePrelimsBulkText = (text) => {
    if (!text || !text.trim()) return [];
    const questionBlocks = text.split(/(?:^|\n)(?=(?:Q\s*\d+[.)]|Question\s*\d+[.:]|\d+[.)]))/i);
    const parsed = [];

    // Existing questions in this folder to check for duplicates
    const existingInFolder = (prelimsQuestionBank || []).filter(
      (q) => (q?.subject || "").trim().toLowerCase() === (activePrelimsFolder || "").toLowerCase()
    );

    questionBlocks.forEach((block, index) => {
      const trimmed = block.trim();
      if (!trimmed) return;

      const qNumMatch = trimmed.match(/^(?:Q\s*(\d+)[.)]|Question\s*(\d+)[.:]|(\d+)[.)])/i);
      const qNumber = qNumMatch ? parseInt(qNumMatch[1] || qNumMatch[2] || qNumMatch[3], 10) : index + 1;

      const coMatch = trimmed.match(/<CO>\s*\(?([A-Da-d])\)?/i);
      const correctAnswer = coMatch ? coMatch[1].toUpperCase() : null;

      let body = trimmed.replace(/^(?:Q\s*\d+[.)]|Question\s*\d+[.:]|\d+[.)])\s*/i, "");

      const optAMatch = body.match(/\(?A\)[.\s]+([\s\S]*?)(?=\(?B\)[.\s]+)/i);
      const optBMatch = body.match(/\(?B\)[.\s]+([\s\S]*?)(?=\(?C\)[.\s]+)/i);
      const optCMatch = body.match(/\(?C\)[.\s]+([\s\S]*?)(?=\(?D\)[.\s]+)/i);
      const optDMatch = body.match(/\(?D\)[.\s]+([\s\S]*?)(?=(?:Options|<CO>|$))/i);

      const qTextMatch = body.match(/^([\s\S]*?)(?=\(?A\)[.\s]+)/i);
      const questionText = qTextMatch ? qTextMatch[1].trim() : body.split("\n")[0].trim();

      const optionA = optAMatch ? optAMatch[1].trim() : "";
      const optionB = optBMatch ? optBMatch[1].trim() : "";
      const optionC = optCMatch ? optCMatch[1].trim() : "";
      const optionD = optDMatch ? optDMatch[1].trim() : "";

      const errors = [];
      if (!questionText) errors.push("Missing question text");
      if (!optionA) errors.push("Missing Option A");
      if (!optionB) errors.push("Missing Option B");
      if (!optionC) errors.push("Missing Option C");
      if (!optionD) errors.push("Missing Option D");
      if (!correctAnswer) errors.push("Missing or invalid <CO> tag");

      const isDuplicate = existingInFolder.some(
        (eq) => (eq?.questionText || "").trim().toLowerCase() === (questionText || "").toLowerCase()
      );
      if (isDuplicate) errors.push("Duplicate question in this folder");

      parsed.push({
        id: `parsed-p-${Date.now()}-${index}`,
        questionNumber: qNumber,
        questionText: questionText || "Sample question text",
        options: {
          A: optionA,
          B: optionB,
          C: optionC,
          D: optionD,
        },
        correctAnswer: correctAnswer || "A",
        subject: activePrelimsFolder, // Automatically locked to this folder!
        topic: "General Subject Focus",
        difficulty: "Medium",
        explanation: "Correct answer parsed from <CO> marker.",
        isValid: errors.length === 0,
        errors,
      });
    });

    return parsed;
  };

  const parseMainsBulkText = (text) => {
    if (!text || !text.trim()) return [];
    const blocks = text.split(/(?:^|\n)(?=(?:Q\s*\d+[.)]|Question\s*\d+[.:]|\d+[.)]))/i);
    const parsed = [];

    const existingInFolder = (mainsQuestionBank || []).filter(
      (q) =>
        (q?.gsPaper || "").trim().toLowerCase() === (activeMainsFolder?.gsPaper || "").toLowerCase() &&
        (q?.subject || "").trim().toLowerCase() === (activeMainsFolder?.subject || "").toLowerCase()
    );

    blocks.forEach((block, index) => {
      const trimmed = block.trim();
      if (!trimmed) return;

      const lines = trimmed.split("\n").map((l) => l.trim()).filter(Boolean);
      let qPrompt = lines[0] || "";
      qPrompt = qPrompt.replace(/^(?:Q\s*\d+[.)]|Question\s*\d+[.:]|\d+[.)])\s*/i, "").trim();

      let reference = "";
      const refIdx = lines.findIndex((l) => l.toLowerCase().startsWith("reference:"));
      if (refIdx !== -1) {
        reference = lines.slice(refIdx).join(" ").replace(/^reference:\s*/i, "");
      }

      const errors = [];
      if (!qPrompt) errors.push("Missing question prompt");

      const isDuplicate = existingInFolder.some(
        (eq) => (eq?.questionText || "").trim().toLowerCase() === qPrompt.toLowerCase()
      );
      if (isDuplicate) errors.push("Duplicate question in this folder");

      parsed.push({
        id: `parsed-m-${Date.now()}-${index}`,
        questionText: qPrompt,
        gsPaper: activeMainsFolder?.gsPaper, // Auto-locked!
        subject: activeMainsFolder?.subject, // Auto-locked!
        topic: "Analytical Focus",
        maxMarks: 15,
        wordLimit: 250,
        difficulty: "Medium",
        referenceAnswer: reference || "Model answer structure / points.",
        isValid: errors.length === 0,
        errors,
      });
    });

    return parsed;
  };

  const handleParseBulk = () => {
    if (!bulkRawText.trim()) {
      alert("Please enter or paste text to parse.");
      return;
    }
    if (activeTab === "prelims") {
      const results = parsePrelimsBulkText(bulkRawText);
      setBulkParsedQuestions(results);
    } else {
      const results = parseMainsBulkText(bulkRawText);
      setBulkParsedQuestions(results);
    }
    setIsBulkParsed(true);
  };

  const [isParsingFile, setIsParsingFile] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    setIsParsingFile(true);

    // Call server-side parsing API for TXT, DOCX, or PDF
    try {
      const serverRes = await qbAPI.parseBulkUpload(file);
      if (serverRes.success && Array.isArray(serverRes.questions) && serverRes.questions.length > 0) {
        const mapped = serverRes.questions.map((q, idx) => ({
          id: `parsed-server-${Date.now()}-${idx}`,
          questionText: q.questionText,
          subject: activePrelimsFolder?.name || activePrelimsFolder || activeMainsFolder?.subject || "General",
          topic: "General",
          difficulty: q.difficulty || "Medium",
          options: q.options || [],
          correctAnswer: q.correctAnswer || "",
          explanation: q.explanation || "",
          isValid: q.isValid,
          errors: q.validationErrors || [],
        }));
        setBulkParsedQuestions(mapped);
        setIsBulkParsed(true);
        setIsParsingFile(false);
        return;
      }
    } catch (apiErr) {
      console.warn("Server-side bulk upload parse failed, attempting local fallback:", apiErr.message);
    }

    // Fallback for TXT files if offline or network error
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setBulkRawText(text);
      if (activeTab === "prelims") {
        setBulkParsedQuestions(parsePrelimsBulkText(text));
      } else {
        setBulkParsedQuestions(parseMainsBulkText(text));
      }
      setIsBulkParsed(true);
      setIsParsingFile(false);
    };
    reader.readAsText(file);
  };

  const handleImportBulk = async () => {
    const validQuestions = bulkParsedQuestions.filter((q) => q.isValid);
    if (validQuestions.length === 0) {
      alert("No valid questions found to import. Please check validation errors.");
      return;
    }

    // Find folderId from dbFolders if available
    let targetFolderId = null;
    if (activeTab === "prelims") {
      const folderName = activePrelimsFolder?.name || activePrelimsFolder;
      const foundFolder = (dbFolders || []).find(
        (f) => f.type === "prelims" && (f.name === folderName || f.subject === folderName)
      );
      targetFolderId = foundFolder?._id || foundFolder?.id;
    } else {
      const foundFolder = (dbFolders || []).find(
        (f) => f.type === "mains" && f.gsPaper === activeMainsFolder?.gsPaper && f.subject === activeMainsFolder?.subject
      );
      targetFolderId = foundFolder?._id || foundFolder?.id;
    }

    if (targetFolderId) {
      try {
        await qbAPI.confirmBulk({
          folderId: targetFolderId,
          examType: activeTab,
          subject: activePrelimsFolder?.name || activePrelimsFolder || activeMainsFolder?.subject || "General",
          questions: validQuestions,
        });
        if (refreshAllData) refreshAllData();
      } catch (err) {
        console.warn("Backend confirm bulk failed, saving in local state:", err.message);
      }
    }

    if (activeTab === "prelims") {
      bulkAddPrelimsQuestions(validQuestions);
    } else {
      bulkAddMainsQuestions(validQuestions);
    }

    setIsBulkModalOpen(false);
    setBulkRawText("");
    setBulkParsedQuestions([]);
    setIsBulkParsed(false);
    setBulkFileName("");
  };

  // --------------------------------------------------------------------------
  // Manual Add / Edit Handlers
  // --------------------------------------------------------------------------
  const openAddModal = () => {
    setEditingQuestion(null);
    if (activeTab === "prelims") {
      setPrelimsForm({
        questionText: "",
        optionA: "",
        optionB: "",
        optionC: "",
        optionD: "",
        correctAnswer: "A",
        topic: "",
        difficulty: "Medium",
        explanation: "",
      });
    } else {
      setMainsForm({
        questionText: "",
        topic: "",
        maxMarks: 15,
        wordLimit: 250,
        difficulty: "Medium",
        referenceAnswer: "",
      });
    }
    setIsAddModalOpen(true);
  };

  const openEditModal = (q) => {
    setEditingQuestion(q);
    if (activeTab === "prelims") {
      setPrelimsForm({
        questionText: q.questionText || "",
        optionA: q.options?.A || "",
        optionB: q.options?.B || "",
        optionC: q.options?.C || "",
        optionD: q.options?.D || "",
        correctAnswer: q.correctAnswer || "A",
        topic: q.topic || "",
        difficulty: q.difficulty || "Medium",
        explanation: q.explanation || "",
      });
    } else {
      setMainsForm({
        questionText: q.questionText || "",
        topic: q.topic || "",
        maxMarks: q.maxMarks || 15,
        wordLimit: q.wordLimit || 250,
        difficulty: q.difficulty || "Medium",
        referenceAnswer: q.referenceAnswer || "",
      });
    }
    setIsEditModalOpen(true);
  };

  const handleSaveQuestion = (e) => {
    e.preventDefault();

    if (activeTab === "prelims") {
      if (!prelimsForm.questionText.trim()) {
        alert("Please enter question text.");
        return;
      }
      const data = {
        questionText: prelimsForm.questionText,
        options: {
          A: prelimsForm.optionA,
          B: prelimsForm.optionB,
          C: prelimsForm.optionC,
          D: prelimsForm.optionD,
        },
        correctAnswer: prelimsForm.correctAnswer,
        subject: activePrelimsFolder, // Lock to folder!
        topic: prelimsForm.topic || "General Subject",
        difficulty: prelimsForm.difficulty,
        explanation: prelimsForm.explanation,
      };

      if (editingQuestion) {
        updatePrelimsQuestion(editingQuestion.id, data);
        setIsEditModalOpen(false);
      } else {
        addPrelimsQuestion(data);
        setIsAddModalOpen(false);
      }
    } else {
      if (!mainsForm.questionText.trim()) {
        alert("Please enter Mains question prompt.");
        return;
      }
      const data = {
        questionText: mainsForm.questionText,
        gsPaper: activeMainsFolder.gsPaper, // Lock to folder!
        subject: activeMainsFolder.subject, // Lock to folder!
        topic: mainsForm.topic || "General Focus",
        maxMarks: Number(mainsForm.maxMarks),
        wordLimit: Number(mainsForm.wordLimit),
        difficulty: mainsForm.difficulty,
        referenceAnswer: mainsForm.referenceAnswer,
      };

      if (editingQuestion) {
        updateMainsQuestion(editingQuestion.id, data);
        setIsEditModalOpen(false);
      } else {
        addMainsQuestion(data);
        setIsAddModalOpen(false);
      }
    }
  };

  // --------------------------------------------------------------------------
  // Move Questions Handler
  // --------------------------------------------------------------------------
  const openMoveModal = (singleItem = null) => {
    setMoveSingleItem(singleItem);
    if (activeTab === "prelims") {
      const destinations = prelimsSubjectFolders.filter((f) => f !== activePrelimsFolder);
      setTargetSubject(destinations[0] || prelimsSubjectFolders[0]);
    } else {
      setTargetGsPaper("GS Paper I");
      setTargetSubject(mainsStructure["GS Paper I"][0]);
    }
    setIsMoveModalOpen(true);
  };

  const handleConfirmMove = () => {
    const idsToMove = moveSingleItem ? [moveSingleItem.id] : selectedIds;
    if (idsToMove.length === 0) return;

    if (activeTab === "prelims") {
      movePrelimsQuestions(idsToMove, targetSubject);
    } else {
      moveMainsQuestions(idsToMove, targetSubject, targetGsPaper);
    }

    setSelectedIds([]);
    setIsMoveModalOpen(false);
    setMoveSingleItem(null);
  };

  // --------------------------------------------------------------------------
  // Bulk Delete
  // --------------------------------------------------------------------------
  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (activeTab === "prelims") {
      bulkDeletePrelimsQuestions(selectedIds);
    } else {
      bulkDeleteMainsQuestions(selectedIds);
    }
    setSelectedIds([]);
    setIsBulkDeleteModalOpen(false);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Top Header & Overview */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
            <FolderOpen className="w-6 h-6 text-red-900" />
            Question Bank
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Subject-wise folder repository for UPSC Prelims & Mains examination questions.
          </p>
        </div>

        {/* Question Bank Centered Tabs */}
        <div className="inline-flex p-1 bg-white border border-slate-200/90 rounded-2xl shadow-xs self-start sm:self-auto">
          <button
            onClick={() => {
              setActiveTab("prelims");
              setActivePrelimsFolder(null);
              setSelectedIds([]);
            }}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === "prelims"
                ? "bg-red-950 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <CheckSquare size={14} />
            <span>Prelims Folders</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                activeTab === "prelims"
                  ? "bg-red-800 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {(prelimsQuestionBank || []).length}
            </span>
          </button>

          <button
            onClick={() => {
              setActiveTab("mains");
              setActiveMainsFolder(null);
              setSelectedIds([]);
            }}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === "mains"
                ? "bg-red-950 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <PenTool size={14} />
            <span>Mains Folders</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                activeTab === "mains"
                  ? "bg-red-800 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {(mainsQuestionBank || []).length}
            </span>
          </button>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* VIEW A: PRELIMS QUESTION BANK                                         */}
      {/* ==================================================================== */}
      {activeTab === "prelims" && (
        <div className="space-y-6">
          {/* If NO folder is open: Show Folder Grid */}
          {!activePrelimsFolder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Prelims Subject Folders
                  </span>
                  <span className="text-xs text-slate-400">
                    ({prelimsSubjectFolders.length} Folders)
                  </span>
                </div>
                <span className="text-xs text-slate-500">
                  Select a folder to manage or upload questions directly
                </span>
              </div>

              {/* 8 Standard Subject Folders Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {prelimsSubjectFolders.map((subject) => {
                  const count = getPrelimsFolderCount(subject);
                  const lastDate = getPrelimsFolderLastDate(subject);

                  return (
                    <div
                      key={subject}
                      onClick={() => {
                        setActivePrelimsFolder(subject);
                        setFolderSearch("");
                        setDifficultyFilter("All");
                        setSelectedIds([]);
                      }}
                      className="group bg-white rounded-2xl border border-slate-200/90 hover:border-red-900/40 p-4 transition-all duration-200 hover:shadow-md cursor-pointer flex flex-col justify-between"
                    >
                      <div className="flex items-start justify-between">
                        <div className="w-11 h-11 rounded-xl bg-red-900/10 text-red-900 group-hover:bg-red-900 group-hover:text-white transition-colors flex items-center justify-center">
                          <Folder className="w-6 h-6" />
                        </div>
                        <span className="px-2.5 py-1 text-xs font-bold text-slate-700 bg-slate-100 rounded-full group-hover:bg-red-50 group-hover:text-red-900 transition-colors">
                          {count} {count === 1 ? "Question" : "Questions"}
                        </span>
                      </div>

                      <div className="mt-4">
                        <h3 className="text-sm font-bold text-slate-900 group-hover:text-red-950 transition-colors">
                          {subject}
                        </h3>
                        <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5">
                          <Calendar size={12} className="text-slate-400" />
                          <span>Updated: {lastDate}</span>
                        </p>
                      </div>

                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-slate-500 group-hover:text-red-900">
                        <span>Open Folder</span>
                        <ChevronRight
                          size={15}
                          className="transform group-hover:translate-x-1 transition-transform"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* If A FOLDER IS OPEN: Inside Folder View */}
          {activePrelimsFolder && (
            <div className="space-y-4">
              {/* Breadcrumb Navigation */}
              <nav className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-white px-4 py-2.5 rounded-xl border border-slate-200/90 shadow-2xs">
                <button
                  onClick={() => {
                    setActivePrelimsFolder(null);
                    setSelectedIds([]);
                  }}
                  className="hover:text-red-900 flex items-center gap-1 font-semibold"
                >
                  <ArrowLeft size={14} /> Back to Folders
                </button>
                <ChevronRight size={14} className="text-slate-300" />
                <span className="text-slate-700 font-semibold">Prelims</span>
                <ChevronRight size={14} className="text-slate-300" />
                <span className="text-red-950 font-bold flex items-center gap-1.5 bg-red-50 text-red-900 px-2 py-0.5 rounded-md">
                  <FolderOpen size={13} /> {activePrelimsFolder}
                </span>
                <span className="ml-auto text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full font-mono">
                  {currentQuestions.length} Questions
                </span>
              </nav>

              {/* Folder Actions & Controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-200/90 shadow-xs">
                {/* Search & Difficulty Filter */}
                <div className="flex flex-1 items-center gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={folderSearch}
                      onChange={(e) => setFolderSearch(e.target.value)}
                      placeholder={`Search ${activePrelimsFolder} questions...`}
                      className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                    />
                  </div>

                  <select
                    value={difficultyFilter}
                    onChange={(e) => setDifficultyFilter(e.target.value)}
                    className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700"
                  >
                    <option value="All">All Difficulties</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                {/* Primary Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setBulkRawText("");
                      setBulkParsedQuestions([]);
                      setIsBulkParsed(false);
                      setBulkFileName("");
                      setIsBulkModalOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                  >
                    <Upload size={14} /> Bulk Upload
                  </button>

                  <button
                    onClick={openAddModal}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-xs transition-colors"
                  >
                    <Plus size={15} /> Add Question Manually
                  </button>
                </div>
              </div>

              {/* Batch Action Toolbar when questions are selected */}
              {selectedIds.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-red-900/10 border border-red-900/30 rounded-xl animate-in fade-in duration-150">
                  <span className="text-xs font-bold text-red-950 flex items-center gap-1.5">
                    <CheckCircle2 size={15} className="text-red-900" />
                    {selectedIds.length} question(s) selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openMoveModal(null)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-slate-800 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg shadow-2xs"
                    >
                      <MoveRight size={13} className="text-red-900" /> Move to Folder
                    </button>
                    <button
                      onClick={() => setIsBulkDeleteModalOpen(true)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-lg shadow-2xs"
                    >
                      <Trash2 size={13} /> Delete Selected
                    </button>
                    <button
                      onClick={() => setSelectedIds([])}
                      className="px-2 py-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
                    >
                      Deselect
                    </button>
                  </div>
                </div>
              )}

              {/* Questions Table inside current folder */}
              <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                        <th className="py-3 px-3 w-10 text-center">
                          <input
                            type="checkbox"
                            checked={
                              currentQuestions.length > 0 &&
                              selectedIds.length === currentQuestions.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(currentQuestions.map((q) => q.id));
                              } else {
                                setSelectedIds([]);
                              }
                            }}
                            className="rounded text-red-900 focus:ring-red-900"
                          />
                        </th>
                        <th className="py-3 px-3 w-16">#</th>
                        <th className="py-3 px-3">Question &amp; Options</th>
                        <th className="py-3 px-3 w-28 text-center">Correct Answer</th>
                        <th className="py-3 px-3 w-24 text-center">Difficulty</th>
                        <th className="py-3 px-3 w-24 text-center">Date</th>
                        <th className="py-3 px-3 w-28 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {currentQuestions.map((q, idx) => {
                        const isSelected = selectedIds.includes(q.id);
                        return (
                          <tr
                            key={q.id}
                            className={`hover:bg-slate-50/80 transition-colors ${
                              isSelected ? "bg-red-50/30" : ""
                            }`}
                          >
                            <td className="py-3 px-3 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedIds((prev) => [...prev, q.id]);
                                  } else {
                                    setSelectedIds((prev) => prev.filter((id) => id !== q.id));
                                  }
                                }}
                                className="rounded text-red-900 focus:ring-red-900"
                              />
                            </td>
                            <td className="py-3 px-3 font-mono font-bold text-slate-500">
                              Q{idx + 1}
                            </td>
                            <td className="py-3 px-3 max-w-md">
                              <p className="font-semibold text-slate-900 line-clamp-2 leading-relaxed">
                                {q.questionText}
                              </p>
                              <div className="flex flex-wrap gap-1.5 mt-1 text-[11px] text-slate-500">
                                <span className="font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                                  A: {q.options?.A ? q.options.A.substring(0, 30) : "—"}
                                </span>
                                <span className="font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                                  B: {q.options?.B ? q.options.B.substring(0, 30) : "—"}
                                </span>
                              </div>
                              {q.topic && (
                                <p className="text-[10px] text-slate-400 mt-1 italic">
                                  Topic: {q.topic}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-extrabold bg-emerald-100 text-emerald-800">
                                &lt;CO&gt; {q.correctAnswer}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span
                                className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-md ${
                                  q.difficulty === "Easy"
                                    ? "bg-blue-50 text-blue-700"
                                    : q.difficulty === "Hard"
                                    ? "bg-rose-50 text-rose-700"
                                    : "bg-amber-50 text-amber-700"
                                }`}
                              >
                                {q.difficulty || "Medium"}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center font-mono text-[11px] text-slate-500">
                              {q.dateAdded || "—"}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setViewingQuestion(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                  title="View Details"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => openEditModal(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-900 hover:bg-red-50"
                                  title="Edit"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() => openMoveModal(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-amber-800 hover:bg-amber-50"
                                  title="Move to another folder"
                                >
                                  <MoveRight size={14} />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmItem(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-700 hover:bg-rose-50"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {currentQuestions.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-12 text-center">
                            <div className="max-w-xs mx-auto text-slate-400">
                              <Folder className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                              <p className="text-xs font-semibold text-slate-700">
                                No questions found in this folder
                              </p>
                              <p className="text-[11px] text-slate-400 mt-1">
                                Add questions manually or bulk upload directly into {activePrelimsFolder}.
                              </p>
                              <div className="flex justify-center gap-2 mt-3">
                                <button
                                  onClick={openAddModal}
                                  className="px-3 py-1.5 text-xs font-bold text-white bg-red-900 rounded-lg hover:bg-red-800 shadow-xs"
                                >
                                  Add Question
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* VIEW B: MAINS QUESTION BANK (Organized by GS Paper)                  */}
      {/* ==================================================================== */}
      {activeTab === "mains" && (
        <div className="space-y-6">
          {/* If NO folder is open: Show GS Paper sections & folders */}
          {!activeMainsFolder && (
            <div className="space-y-8">
              {Object.entries(mainsStructure).map(([gsPaper, subjects]) => {
                return (
                  <div key={gsPaper} className="space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-900" />
                        <h2 className="text-sm font-bold text-slate-900 tracking-wide uppercase">
                          {gsPaper}
                        </h2>
                        <span className="text-xs text-slate-400">
                          ({subjects.length} Subject Folders)
                        </span>
                      </div>
                      <span className="text-xs text-slate-500 font-medium">
                        Click folder to view questions
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {subjects.map((sub) => {
                        const count = getMainsFolderCount(gsPaper, sub);
                        const lastDate = getMainsFolderLastDate(gsPaper, sub);

                        return (
                          <div
                            key={sub}
                            onClick={() => {
                              setActiveMainsFolder({ gsPaper, subject: sub });
                              setFolderSearch("");
                              setDifficultyFilter("All");
                              setSelectedIds([]);
                            }}
                            className="group bg-white rounded-2xl border border-slate-200/90 hover:border-red-900/40 p-4 transition-all duration-200 hover:shadow-md cursor-pointer flex flex-col justify-between"
                          >
                            <div className="flex items-start justify-between">
                              <div className="w-11 h-11 rounded-xl bg-red-900/10 text-red-900 group-hover:bg-red-900 group-hover:text-white transition-colors flex items-center justify-center">
                                <Folder className="w-6 h-6" />
                              </div>
                              <span className="px-2.5 py-1 text-xs font-bold text-slate-700 bg-slate-100 rounded-full group-hover:bg-red-50 group-hover:text-red-900 transition-colors">
                                {count} {count === 1 ? "Question" : "Questions"}
                              </span>
                            </div>

                            <div className="mt-4">
                              <h3 className="text-sm font-bold text-slate-900 group-hover:text-red-950 transition-colors">
                                {sub}
                              </h3>
                              <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5">
                                <Calendar size={12} className="text-slate-400" />
                                <span>Updated: {lastDate}</span>
                              </p>
                            </div>

                            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-slate-500 group-hover:text-red-900">
                              <span>Open Folder</span>
                              <ChevronRight
                                size={15}
                                className="transform group-hover:translate-x-1 transition-transform"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* If A MAINS FOLDER IS OPEN */}
          {activeMainsFolder && (
            <div className="space-y-4">
              {/* Breadcrumb Navigation */}
              <nav className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-white px-4 py-2.5 rounded-xl border border-slate-200/90 shadow-2xs">
                <button
                  onClick={() => {
                    setActiveMainsFolder(null);
                    setSelectedIds([]);
                  }}
                  className="hover:text-red-900 flex items-center gap-1 font-semibold"
                >
                  <ArrowLeft size={14} /> Back to Folders
                </button>
                <ChevronRight size={14} className="text-slate-300" />
                <span className="text-slate-700 font-semibold">Mains</span>
                <ChevronRight size={14} className="text-slate-300" />
                <span className="text-slate-700 font-semibold">{activeMainsFolder.gsPaper}</span>
                <ChevronRight size={14} className="text-slate-300" />
                <span className="text-red-950 font-bold flex items-center gap-1.5 bg-red-50 text-red-900 px-2 py-0.5 rounded-md">
                  <FolderOpen size={13} /> {activeMainsFolder.subject}
                </span>
                <span className="ml-auto text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full font-mono">
                  {currentQuestions.length} Questions
                </span>
              </nav>

              {/* Controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-200/90 shadow-xs">
                <div className="flex flex-1 items-center gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={folderSearch}
                      onChange={(e) => setFolderSearch(e.target.value)}
                      placeholder={`Search ${activeMainsFolder.subject} questions...`}
                      className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                    />
                  </div>

                  <select
                    value={difficultyFilter}
                    onChange={(e) => setDifficultyFilter(e.target.value)}
                    className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700"
                  >
                    <option value="All">All Difficulties</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setBulkRawText("");
                      setBulkParsedQuestions([]);
                      setIsBulkParsed(false);
                      setBulkFileName("");
                      setIsBulkModalOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                  >
                    <Upload size={14} /> Bulk Upload
                  </button>

                  <button
                    onClick={openAddModal}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-xs transition-colors"
                  >
                    <Plus size={15} /> Add Mains Question
                  </button>
                </div>
              </div>

              {/* Batch Action Toolbar */}
              {selectedIds.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-red-900/10 border border-red-900/30 rounded-xl">
                  <span className="text-xs font-bold text-red-950 flex items-center gap-1.5">
                    <CheckCircle2 size={15} className="text-red-900" />
                    {selectedIds.length} Mains question(s) selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openMoveModal(null)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-slate-800 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg shadow-2xs"
                    >
                      <MoveRight size={13} className="text-red-900" /> Move Folder
                    </button>
                    <button
                      onClick={() => setIsBulkDeleteModalOpen(true)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-lg shadow-2xs"
                    >
                      <Trash2 size={13} /> Delete Selected
                    </button>
                    <button
                      onClick={() => setSelectedIds([])}
                      className="px-2 py-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
                    >
                      Deselect
                    </button>
                  </div>
                </div>
              )}

              {/* Questions Table inside Mains Folder */}
              <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                        <th className="py-3 px-3 w-10 text-center">
                          <input
                            type="checkbox"
                            checked={
                              currentQuestions.length > 0 &&
                              selectedIds.length === currentQuestions.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(currentQuestions.map((q) => q.id));
                              } else {
                                setSelectedIds([]);
                              }
                            }}
                            className="rounded text-red-900 focus:ring-red-900"
                          />
                        </th>
                        <th className="py-3 px-3">Mains Question Prompt</th>
                        <th className="py-3 px-3 w-28 text-center">Marks / Words</th>
                        <th className="py-3 px-3 w-24 text-center">Difficulty</th>
                        <th className="py-3 px-3 w-28 text-center">Date Added</th>
                        <th className="py-3 px-3 w-28 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {currentQuestions.map((q) => {
                        const isSelected = selectedIds.includes(q.id);
                        return (
                          <tr
                            key={q.id}
                            className={`hover:bg-slate-50/80 transition-colors ${
                              isSelected ? "bg-red-50/30" : ""
                            }`}
                          >
                            <td className="py-3 px-3 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedIds((prev) => [...prev, q.id]);
                                  } else {
                                    setSelectedIds((prev) => prev.filter((id) => id !== q.id));
                                  }
                                }}
                                className="rounded text-red-900 focus:ring-red-900"
                              />
                            </td>
                            <td className="py-3 px-3 max-w-lg">
                              <p className="font-serif font-medium text-slate-900 leading-relaxed line-clamp-3">
                                {q.questionText}
                              </p>
                              {q.topic && (
                                <p className="text-[10px] text-slate-400 mt-1 italic">
                                  Topic: {q.topic}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="font-bold text-slate-800">
                                {q.maxMarks || 15} M
                              </span>
                              <span className="text-[11px] text-slate-400 block font-mono">
                                {q.wordLimit || 250} w
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span
                                className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-md ${
                                  q.difficulty === "Easy"
                                    ? "bg-blue-50 text-blue-700"
                                    : q.difficulty === "Hard"
                                    ? "bg-rose-50 text-rose-700"
                                    : "bg-amber-50 text-amber-700"
                                }`}
                              >
                                {q.difficulty || "Medium"}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center font-mono text-[11px] text-slate-500">
                              {q.dateAdded || "—"}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setViewingQuestion(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                  title="View Details"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => openEditModal(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-900 hover:bg-red-50"
                                  title="Edit"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() => openMoveModal(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-amber-800 hover:bg-amber-50"
                                  title="Move to another folder"
                                >
                                  <MoveRight size={14} />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmItem(q)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-700 hover:bg-rose-50"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {currentQuestions.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-12 text-center">
                            <div className="max-w-xs mx-auto text-slate-400">
                              <Folder className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                              <p className="text-xs font-semibold text-slate-700">
                                No Mains questions found in this folder
                              </p>
                              <p className="text-[11px] text-slate-400 mt-1">
                                Add questions directly into {activeMainsFolder.gsPaper} &gt; {activeMainsFolder.subject}.
                              </p>
                              <div className="flex justify-center gap-2 mt-3">
                                <button
                                  onClick={openAddModal}
                                  className="px-3 py-1.5 text-xs font-bold text-white bg-red-900 rounded-lg hover:bg-red-800 shadow-xs"
                                >
                                  Add Question
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* MODAL 1: ADD / EDIT QUESTION (Auto-assigned Subject from Folder)     */}
      {/* ==================================================================== */}
      {(isAddModalOpen || isEditModalOpen) && (
        <Modal
          isOpen={isAddModalOpen || isEditModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
            setEditingQuestion(null);
          }}
          title={
            editingQuestion
              ? `Edit ${activeTab === "prelims" ? "Prelims" : "Mains"} Question`
              : `Add Question to ${
                  activeTab === "prelims"
                    ? activePrelimsFolder
                    : `${activeMainsFolder?.gsPaper} > ${activeMainsFolder?.subject}`
                }`
          }
          maxWidth="max-w-2xl"
        >
          <form onSubmit={handleSaveQuestion} className="space-y-4">
            {/* Folder Lock Indicator */}
            <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-500">Destination Folder:</span>
              <span className="font-bold text-red-900 bg-red-100/70 px-2.5 py-1 rounded-md flex items-center gap-1.5">
                <FolderOpen size={13} />
                {activeTab === "prelims"
                  ? activePrelimsFolder
                  : `${activeMainsFolder?.gsPaper} • ${activeMainsFolder?.subject}`}
              </span>
            </div>

            {/* PRELIMS FIELDS */}
            {activeTab === "prelims" && (
              <>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Question Text *
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={prelimsForm.questionText}
                    onChange={(e) =>
                      setPrelimsForm({ ...prelimsForm, questionText: e.target.value })
                    }
                    placeholder="Enter the complete UPSC Prelims question statement..."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Options (A, B, C, D) *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {["A", "B", "C", "D"].map((opt) => (
                      <div key={opt} className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-lg bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                          {opt}
                        </span>
                        <input
                          type="text"
                          required
                          value={prelimsForm[`option${opt}`]}
                          onChange={(e) =>
                            setPrelimsForm({
                              ...prelimsForm,
                              [`option${opt}`]: e.target.value,
                            })
                          }
                          placeholder={`Option ${opt} text...`}
                          className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Correct Answer (&lt;CO&gt;) *
                    </label>
                    <select
                      value={prelimsForm.correctAnswer}
                      onChange={(e) =>
                        setPrelimsForm({ ...prelimsForm, correctAnswer: e.target.value })
                      }
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-emerald-800"
                    >
                      <option value="A">Option A</option>
                      <option value="B">Option B</option>
                      <option value="C">Option C</option>
                      <option value="D">Option D</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Topic / Sub-topic
                    </label>
                    <input
                      type="text"
                      value={prelimsForm.topic}
                      onChange={(e) =>
                        setPrelimsForm({ ...prelimsForm, topic: e.target.value })
                      }
                      placeholder="e.g. Fundamental Rights"
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Difficulty Level
                    </label>
                    <select
                      value={prelimsForm.difficulty}
                      onChange={(e) =>
                        setPrelimsForm({ ...prelimsForm, difficulty: e.target.value })
                      }
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700"
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Detailed Explanation
                  </label>
                  <textarea
                    rows={2}
                    value={prelimsForm.explanation}
                    onChange={(e) =>
                      setPrelimsForm({ ...prelimsForm, explanation: e.target.value })
                    }
                    placeholder="Reference rationale explaining why this option is correct..."
                    className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800"
                  />
                </div>
              </>
            )}

            {/* MAINS FIELDS */}
            {activeTab === "mains" && (
              <>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Mains Analytical Prompt *
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={mainsForm.questionText}
                    onChange={(e) =>
                      setMainsForm({ ...mainsForm, questionText: e.target.value })
                    }
                    placeholder="Enter the complete Mains question prompt..."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-serif text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Max Marks
                    </label>
                    <select
                      value={mainsForm.maxMarks}
                      onChange={(e) =>
                        setMainsForm({ ...mainsForm, maxMarks: Number(e.target.value) })
                      }
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                    >
                      <option value={10}>10 Marks</option>
                      <option value={15}>15 Marks</option>
                      <option value={20}>20 Marks</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Word Limit
                    </label>
                    <select
                      value={mainsForm.wordLimit}
                      onChange={(e) =>
                        setMainsForm({ ...mainsForm, wordLimit: Number(e.target.value) })
                      }
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                    >
                      <option value={150}>150 Words</option>
                      <option value={250}>250 Words</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Difficulty Level
                    </label>
                    <select
                      value={mainsForm.difficulty}
                      onChange={(e) =>
                        setMainsForm({ ...mainsForm, difficulty: e.target.value })
                      }
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700"
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Topic Focus
                    </label>
                    <input
                      type="text"
                      value={mainsForm.topic}
                      onChange={(e) =>
                        setMainsForm({ ...mainsForm, topic: e.target.value })
                      }
                      placeholder="e.g. DRSCs"
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Reference Model Answer / Key Structure
                  </label>
                  <textarea
                    rows={4}
                    value={mainsForm.referenceAnswer}
                    onChange={(e) =>
                      setMainsForm({ ...mainsForm, referenceAnswer: e.target.value })
                    }
                    placeholder="Enter key analytical points, committee references, or structural recommendations..."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800"
                  />
                </div>
              </>
            )}

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditModalOpen(false);
                  setEditingQuestion(null);
                }}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-xs"
              >
                {editingQuestion ? "Save Changes" : "Save to Folder"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ==================================================================== */}
      {/* MODAL 2: BULK UPLOAD INSIDE CURRENT FOLDER                           */}
      {/* ==================================================================== */}
      {isBulkModalOpen && (
        <Modal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          title={`Bulk Upload Questions to ${
            activeTab === "prelims"
              ? activePrelimsFolder
              : `${activeMainsFolder?.gsPaper} > ${activeMainsFolder?.subject}`
          }`}
          maxWidth="max-w-3xl"
        >
          <div className="space-y-4">
            {/* Notice: Auto-Assigned folder banner */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-amber-700 flex-shrink-0" />
                <span>
                  All imported questions will <strong>automatically belong to this folder</strong>.
                </span>
              </div>
              <span className="font-bold bg-amber-100 px-2 py-0.5 rounded text-[11px]">
                {activeTab === "prelims"
                  ? activePrelimsFolder
                  : `${activeMainsFolder?.gsPaper} • ${activeMainsFolder?.subject}`}
              </span>
            </div>

            {/* File Drag/Drop or Select (.txt, .docx, .pdf) */}
            <div className="border-2 border-dashed border-slate-200 hover:border-red-900/50 rounded-2xl p-4 text-center bg-slate-50/50 transition-colors">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-xs font-bold text-slate-700">
                Upload question file (.txt, .docx, .pdf)
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Questions will be automatically parsed using standard format with &lt;CO&gt; tags
              </p>
              <label className="inline-block mt-3 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 text-xs font-bold rounded-xl cursor-pointer shadow-2xs">
                {isParsingFile ? "Parsing on Server..." : "Browse File"}
                <input
                  type="file"
                  accept=".txt,.docx,.pdf"
                  onChange={handleFileUpload}
                  disabled={isParsingFile}
                  className="hidden"
                />
              </label>
              {isParsingFile && (
                <p className="mt-2 text-xs font-semibold text-red-900 animate-pulse">
                  Extracting and analyzing questions via server parser...
                </p>
              )}
              {bulkFileName && !isParsingFile && (
                <p className="mt-2 text-xs font-semibold text-emerald-700 flex items-center justify-center gap-1">
                  <CheckCircle2 size={13} /> Loaded: {bulkFileName}
                </p>
              )}
            </div>

            {/* Paste box */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-700">
                  Or Paste Questions Text Below
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const sample =
                      activeTab === "prelims" ? samplePrelimsRawText : sampleMainsRawText;
                    setBulkRawText(sample);
                    setCopiedTemplate(true);
                    setTimeout(() => setCopiedTemplate(false), 2000);
                  }}
                  className="text-[11px] font-semibold text-red-900 hover:underline flex items-center gap-1"
                >
                  <Copy size={12} />
                  {copiedTemplate ? "Sample Inserted!" : "Load Sample Format"}
                </button>
              </div>

              <textarea
                rows={6}
                value={bulkRawText}
                onChange={(e) => {
                  setBulkRawText(e.target.value);
                  setIsBulkParsed(false);
                }}
                placeholder={
                  activeTab === "prelims"
                    ? `Q1.) Question text\n(A) Option 1\n(B) Option 2\n(C) Option 3\n(D) Option 4\nOptions\n\n<CO> B`
                    : `Q1.) Mains question prompt...\nReference: Key structure points...`
                }
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleParseBulk}
                className="px-4 py-2 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl shadow-xs"
              >
                Parse &amp; Validate Questions
              </button>
            </div>

            {/* Parsed Preview Table */}
            {isBulkParsed && (
              <div className="space-y-3 pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-800">
                    Parsed Preview ({bulkParsedQuestions.length} Questions)
                  </h4>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-emerald-700 font-bold">
                      {bulkParsedQuestions.filter((q) => q.isValid).length} Valid
                    </span>
                    <span>•</span>
                    <span className="text-rose-700 font-bold">
                      {bulkParsedQuestions.filter((q) => !q.isValid).length} Errors
                    </span>
                  </div>
                </div>

                <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl pr-1">
                  {bulkParsedQuestions.map((q, idx) => (
                    <div
                      key={q.id || idx}
                      className={`p-3 text-xs flex items-start gap-3 ${
                        q.isValid ? "hover:bg-slate-50" : "bg-rose-50/40"
                      }`}
                    >
                      <span className="font-mono font-bold text-slate-500">
                        #{idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 line-clamp-2">
                          {q.questionText}
                        </p>
                        {activeTab === "prelims" && (
                          <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
                            <span className="font-extrabold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">
                              &lt;CO&gt; {q.correctAnswer}
                            </span>
                            <span>•</span>
                            <span>A: {q.options?.A?.substring(0, 20)}</span>
                            <span>B: {q.options?.B?.substring(0, 20)}</span>
                          </div>
                        )}
                        {!q.isValid && (
                          <div className="mt-1 text-[11px] text-rose-700 font-semibold flex items-center gap-1">
                            <AlertCircle size={12} /> {q.errors.join(", ")}
                          </div>
                        )}
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${
                          q.isValid
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-rose-100 text-rose-800"
                        }`}
                      >
                        {q.isValid ? "Ready" : "Invalid"}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsBulkModalOpen(false)}
                    className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleImportBulk}
                    disabled={bulkParsedQuestions.filter((q) => q.isValid).length === 0}
                    className="px-5 py-2 text-xs font-bold text-white bg-red-900 hover:bg-red-800 disabled:opacity-50 rounded-xl shadow-xs"
                  >
                    Import {bulkParsedQuestions.filter((q) => q.isValid).length} Valid Questions
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ==================================================================== */}
      {/* MODAL 3: MOVE QUESTIONS TO ANOTHER FOLDER                            */}
      {/* ==================================================================== */}
      {isMoveModalOpen && (
        <Modal
          isOpen={isMoveModalOpen}
          onClose={() => {
            setIsMoveModalOpen(false);
            setMoveSingleItem(null);
          }}
          title="Move Questions to Another Folder"
          maxWidth="max-w-md"
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-600">
              Moving{" "}
              <strong>
                {moveSingleItem ? "1 question" : `${selectedIds.length} questions`}
              </strong>{" "}
              from current folder:
            </p>

            {activeTab === "prelims" ? (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Select Destination Prelims Subject Folder:
                </label>
                <select
                  value={targetSubject}
                  onChange={(e) => setTargetSubject(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800"
                >
                  {prelimsSubjectFolders.map((subj) => (
                    <option key={subj} value={subj}>
                      {subj}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Select Target GS Paper:
                  </label>
                  <select
                    value={targetGsPaper}
                    onChange={(e) => {
                      const paper = e.target.value;
                      setTargetGsPaper(paper);
                      setTargetSubject(mainsStructure[paper][0]);
                    }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800"
                  >
                    {Object.keys(mainsStructure).map((paper) => (
                      <option key={paper} value={paper}>
                        {paper}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Select Target Subject:
                  </label>
                  <select
                    value={targetSubject}
                    onChange={(e) => setTargetSubject(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800"
                  >
                    {(mainsStructure[targetGsPaper] || []).map((sub) => (
                      <option key={sub} value={sub}>
                        {sub}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setIsMoveModalOpen(false);
                  setMoveSingleItem(null);
                }}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmMove}
                className="px-5 py-2 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-xs"
              >
                Confirm Move
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ==================================================================== */}
      {/* MODAL 4: VIEW QUESTION DETAILS                                       */}
      {/* ==================================================================== */}
      {viewingQuestion && (
        <Modal
          isOpen={!!viewingQuestion}
          onClose={() => setViewingQuestion(null)}
          title="Question Details"
          maxWidth="max-w-xl"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold text-red-900 bg-red-100 px-2.5 py-0.5 rounded-full">
                {viewingQuestion.subject}
              </span>
              {viewingQuestion.gsPaper && (
                <span className="font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">
                  {viewingQuestion.gsPaper}
                </span>
              )}
              <span className="text-slate-400">•</span>
              <span className="text-slate-500 font-semibold">
                Difficulty: {viewingQuestion.difficulty || "Medium"}
              </span>
            </div>

            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                Question
              </p>
              <p className="text-sm font-semibold text-slate-900 leading-relaxed whitespace-pre-line">
                {viewingQuestion.questionText}
              </p>
            </div>

            {viewingQuestion.options && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Options
                </p>
                {["A", "B", "C", "D"].map((opt) => {
                  const isCorrect = viewingQuestion.correctAnswer === opt;
                  return (
                    <div
                      key={opt}
                      className={`p-2.5 rounded-xl border text-xs flex items-center justify-between ${
                        isCorrect
                          ? "bg-emerald-50 border-emerald-300 font-bold text-emerald-900"
                          : "bg-slate-50 border-slate-200 text-slate-800"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <strong className="font-mono">({opt})</strong>{" "}
                        {viewingQuestion.options[opt]}
                      </span>
                      {isCorrect && (
                        <span className="text-[11px] font-extrabold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                          &lt;CO&gt; Correct Answer
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {viewingQuestion.referenceAnswer && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Reference Model Answer / Points
                </p>
                <div className="p-3 bg-slate-50 rounded-xl text-xs text-slate-800 whitespace-pre-line leading-relaxed font-sans">
                  {viewingQuestion.referenceAnswer}
                </div>
              </div>
            )}

            {viewingQuestion.explanation && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Explanation
                </p>
                <p className="text-xs text-slate-700 leading-relaxed bg-slate-50 p-2.5 rounded-xl">
                  {viewingQuestion.explanation}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ==================================================================== */}
      {/* MODAL 5: SINGLE DELETE CONFIRMATION                                  */}
      {/* ==================================================================== */}
      {deleteConfirmItem && (
        <Modal
          isOpen={!!deleteConfirmItem}
          onClose={() => setDeleteConfirmItem(null)}
          title="Delete Question"
          maxWidth="max-w-md"
          footer={
            <div className="flex items-center justify-end gap-2 w-full">
              <button
                onClick={() => setDeleteConfirmItem(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (activeTab === "prelims") {
                    deletePrelimsQuestion(deleteConfirmItem.id);
                  } else {
                    deleteMainsQuestion(deleteConfirmItem.id);
                  }
                  setDeleteConfirmItem(null);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-xl shadow-xs"
              >
                Delete
              </button>
            </div>
          }
        >
          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to delete this question from the folder? This action cannot be undone.
          </p>
        </Modal>
      )}

      {/* ==================================================================== */}
      {/* MODAL 6: BULK DELETE CONFIRMATION                                    */}
      {/* ==================================================================== */}
      {isBulkDeleteModalOpen && (
        <Modal
          isOpen={isBulkDeleteModalOpen}
          onClose={() => setIsBulkDeleteModalOpen(false)}
          title="Delete Selected Questions"
          maxWidth="max-w-md"
          footer={
            <div className="flex items-center justify-end gap-2 w-full">
              <button
                onClick={() => setIsBulkDeleteModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-xl shadow-xs"
              >
                Delete {selectedIds.length} Questions
              </button>
            </div>
          }
        >
          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to delete all <strong>{selectedIds.length}</strong> selected questions from this folder?
          </p>
        </Modal>
      )}
    </div>
  );
};

export default QuestionBankManagement;
